import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { LogIn, ShieldAlert, ArrowLeft, Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
  requireAll?: boolean;
}

// Use a module-level flag to track if login has been initiated
// This persists across component remounts and URL changes
let loginInitiated = false;

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  roles = [], 
  requireAll = false 
}) => {
  const { authenticated, loading, hasRole, hasAnyRole, login, keycloak } = useAuth();
  
  // Check if we hit a login_required error (to prevent infinite redirect loop)
  const [showLoginPrompt, setShowLoginPrompt] = useState(() => {
    return sessionStorage.getItem('keycloak_login_required') === 'true';
  });

  useEffect(() => {
    // Reset the flag if user becomes authenticated
    if (authenticated) {
      loginInitiated = false;
      // Clear all login tracking flags when successfully authenticated
      sessionStorage.removeItem('keycloak_login_required');
      sessionStorage.removeItem('keycloak_login_attempted');
      setShowLoginPrompt(false);
    }
  }, [authenticated]);

  useEffect(() => {
    // Only trigger redirect once when:
    // 1. Loading is complete
    // 2. User is not authenticated  
    // 3. We haven't already initiated login
    // 4. Keycloak instance exists
    // 5. We're not in a login_required error state (prevents infinite loop)
    if (!loading && !authenticated && !loginInitiated && keycloak && !showLoginPrompt) {
      loginInitiated = true;
      login();
    }
  }, [loading, authenticated, login, keycloak, showLoginPrompt]);

  const handleManualLogin = () => {
    // Clear the flag and initiate login
    sessionStorage.removeItem('keycloak_login_required');
    setShowLoginPrompt(false);
    loginInitiated = true;
    login();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-900">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    // If we hit login_required error, show a manual login button instead of auto-redirecting
    if (showLoginPrompt) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-zinc-900">
          <Card className="w-full max-w-md mx-4 bg-zinc-800 border-zinc-700">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <LogIn className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl text-zinc-100">Login Required</CardTitle>
              <CardDescription className="text-zinc-400">
                Your session has expired or you need to sign in to access this page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleManualLogin}
                className="w-full"
              >
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    // Show redirecting message - login() is called in useEffect
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-900">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Check role requirements if specified
  if (roles.length > 0) {
    const hasRequiredRoles = requireAll
      ? roles.every(role => hasRole(role))
      : hasAnyRole(roles);

    if (!hasRequiredRoles) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <ShieldAlert className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle className="text-2xl">Access Denied</CardTitle>
              <CardDescription>
                You don't have permission to access this page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => window.history.back()}
                variant="outline"
                className="w-full"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  return <>{children}</>;
};
